<script>
import { mapStores } from "pinia";

import Tooltip from "@/components/article/Tooltip.vue";
import { useSettingsStore } from "@/stores";

import { buildCountTypeHeader, buildGenderHeader } from "./labels";

export default {
  name: "AdjectiveInflectionSchemeModal",
  components: {
    Tooltip
  },
  props: {
    lemma: {
      type: String,
      required: true
    },
    inflectionScheme: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      activeView: Object.keys(this.inflectionScheme)[0],
      specialCases: ["beau", "mou", "fou", "vieux", "nouveau"],
      comparisonMap: {
        positive: 1,
        comparative: 2,
        superlative: 3
      },
      orderedCountTypes: ["singular", "plural"]
    };
  },
  computed: {
    ...mapStores(useSettingsStore),

    wordclassLevel() {
      return this.settingsStore.activeWordClassLevel;
    },
    views() {
      const views = [];

      for (const [comparisonKey, genders] of Object.entries(this.inflectionScheme)) {
        const body = [];
        for (const [genderKey, rawInflections] of Object.entries(genders)) {
          body.push(this.createRow(rawInflections, genderKey));
        }

        views.push({
          comparison: comparisonKey,
          head: this.orderedCountTypes,
          body
        });
      }

      return views;
    }
  },
  methods: {
    buildCountTypeHeader,
    createRow(data, gender) {
      const row = [
        [
          {
            content: buildGenderHeader(this.wordclassLevel, gender)
          }
        ]
      ];

      for (const countType of this.orderedCountTypes) {
        if (!(countType in data)) {
          row.push([{ content: "-" }]);
          continue;
        }

        if (this.specialCases.includes(this.lemma) && gender === "masculine") {
          row.push(this.createSpecialCaseCell(data[countType], countType));
        } else {
          row.push(this.createCell(data[countType]));
        }
      }

      return row;
    },
    createCell(data) {
      const inflections = [];
      for (let i = 0; i < data.length; i++) {
        if (i === 0) {
          inflections.push({
            content: data[i].index
          });

          continue;
        }

        inflections.push({
          content: `&nbsp;/&nbsp;${data[i].inflexion}`
        });
      }

      return inflections;
    },
    createSpecialCaseCell(data, countType) {
      const inflections = [];
      for (let i = 0; i < data.length; i++) {
        const inflection = {};

        if (countType === "singular") {
          inflection.tooltip = i === 0 ? "Foran konsonant" : "Foran vokal og stumt h";
        }

        if (i === 0) {
          inflection.content = data[i].index;
        } else {
          inflection.content = `&nbsp;/&nbsp;${data[i].inflexion}`;
        }

        inflections.push(inflection);
      }

      return inflections;
    }
  }
};
</script>

<template>
  <div>
    <div class="inflection-scheme-modal__tabs">
      <template v-for="view in views" :key="view.comparison">
        <button
          class="inflection-scheme-modal__tab-button"
          :class="{
            'inflection-scheme-modal__tab-button--active': activeView === view.comparison
          }"
          @click="activeView = view.comparison"
        >
          {{ comparisonMap[view.comparison] }}. grad
        </button>
      </template>
    </div>
    <div v-for="view in views" :key="view.comparison">
      <table v-if="activeView === view.comparison" class="table inflection-scheme-modal__table">
        <thead>
          <tr>
            <th scope="col" class="inflection-scheme-modal__th" />
            <th
              v-for="countType in view.head"
              :key="countType"
              scope="col"
              class="inflection-scheme-modal__th"
            >
              {{ buildCountTypeHeader(wordclassLevel, countType) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rowIndex) in view.body"
            :key="rowIndex"
            class="inflection-scheme-modal__tr"
          >
            <td
              v-for="(column, columnIndex) in row"
              :key="rowIndex + columnIndex"
              class="inflection-scheme-modal__td"
            >
              <div class="inflection-scheme-modal__inner-cell">
                <!-- eslint-disable vue/no-v-html -->
                <span v-for="(cell, cellIndex) in column" :key="rowIndex + columnIndex + cellIndex"
                  ><span v-html="cell.content"></span>&nbsp;<Tooltip
                    v-if="cell.tooltip"
                    :text="cell.tooltip"
                    :margin-bottom="8"
                    variant="secondary"
                /></span>
                <!-- eslint-enable vue/no-v-html -->
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
